import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { cloneDeep, round } from 'lodash-es';
import { BehaviorSubject, Subscription } from 'rxjs';
import { Constants } from '../../../common/constants';
import { AndelCombochartBaseComponent } from '../../../abin-resultat-rapport/base-components';
import { AbinResultDto } from '../../../inge-api/abinclient/models';
import { select } from '@ngrx/store';
import { GoogleCombochartComponent } from '../../../google-charts/google-charts-base-components/google-combochart/google-combochart.component';

@Component({
    selector: 'app-rase-forekomst-chart',
    templateUrl: './rase-forekomst-chart.component.html',
    styleUrls: ['./rase-forekomst-chart.component.scss']
})
export class RaseForekomstChartComponent extends AndelCombochartBaseComponent implements OnInit, OnDestroy {
    @ViewChild('chartContainer') chartContainer: ElementRef;

    @ViewChild(GoogleCombochartComponent, { static: true })
    googleCombochart: GoogleCombochartComponent;

    private subs: Subscription[];

    private _diagramBeskrivning = new BehaviorSubject<string>(null);
    diagramBeskrivning$ = this._diagramBeskrivning.asObservable();

    data: any[][];

    @Input()
    set diagramBeskrivning(val: string) {
        if (val) this._diagramBeskrivning.next(val);
    }

    @Input()
    set inventeringar(val: AbinResultDto[]) {
        if (val) {
            const sorted = cloneDeep(val).sort((a, b) => a.invAr - b.invAr);
            this._inventeringar.next(sorted);
        }
    }

    public chartName: string = Constants.DIAGRAM_NAMN.FOREKOMST_RASE;

    ngOnInit(): void {
        this.subs = [];
        this.subs.push(
            this.inventeringar$.subscribe((x) => {
                if (x && x.length > 0) {
                    const data: any[][] = [
                        [
                            'År',
                            { id: 'Arsskada_1ar', type: 'number', label: 'Årsvärde' },
                            { id: 'Medelfel_low', type: 'number', role: 'interval' },
                            { id: 'Medelfel_high', type: 'number', role: 'interval' },
                            { id: 'tool_tip_1ar', type: 'string', role: 'tooltip', p: { html: true } },
                            { id: 'Medel_3ar', type: 'number', label: 'Sammanvägning för de tre senaste inventeringarna' },
                            { id: 'Medelfel_3ar_low', type: 'number', role: 'interval' },
                            { id: 'Medelfel_3ar_high', type: 'number', role: 'interval' },
                            { id: 'tool_tip_3ar', type: 'string', role: 'tooltip', p: { html: true } }
                        ]
                    ];

                    x.forEach((inv) => {
                        if (inv.antalRASEHa || inv.antalRASEHa === 0) {
                            // Årsskada
                            const arsVarde = inv.antalRASEHa;
                            const mfel_arsskada = inv.medelfelAntalRASEHa * 1.96;
                            const mfel_arsVarde_low = (inv.medelfelAntalRASEHa ?? 0) > 0 ? arsVarde - mfel_arsskada : null;
                            const mfel_arsVarde_high = (inv.medelfelAntalRASEHa ?? 0) > 0 ? arsVarde + mfel_arsskada : null;

                            // 3års medel
                            const trearsMedel = inv.antalRASEHa3InvAVG;
                            const mfel_trearsMedel = inv.medelfelAntalRASEHa3InvMedel * 1.96;
                            const mfel_trearsMedel_low = (inv.medelfelAntalRASEHa3InvMedel ?? 0) > 0 ? trearsMedel - mfel_trearsMedel : null;
                            const mfel_trearsMedel_high = (inv.medelfelAntalRASEHa3InvMedel ?? 0) > 0 ? trearsMedel + mfel_trearsMedel : null;

                            data.push([
                                `${inv.invAr}`,
                                arsVarde > 0 ? arsVarde : null,
                                mfel_arsVarde_low,
                                mfel_arsVarde_high,
                                this.getTooltip2(false, arsVarde, mfel_arsskada, 'Förekomst 1 år', 'Förekomst senaste 3 inv', '1.0-0', false),
                                trearsMedel,
                                mfel_trearsMedel_low,
                                mfel_trearsMedel_high,
                                this.getTooltip2(true, trearsMedel, mfel_trearsMedel, 'Förekomst 1 år', 'Förekomst senaste 3 inv', '1.0-0', false)
                            ]);
                        }
                    });
                    this.options = {
                        ...this.options,
                        tooltip: { isHtml: true },
                        interval: {
                            Medelfel_low: { style: 'bars', lineWidth: 1 },
                            Medelfel_high: { style: 'bars', lineWidth: 1 },
                            Medelfel_3ar_low: { style: 'area', curveType: 'none', fillOpacity: 0.48 },
                            Medelfel_3ar_high: { style: 'area', curveType: 'none', fillOpacity: 0.48 }
                        },
                        height: 250,
                        vAxis: {
                            ...this.options.vAxis,
                            title: 'Antal',
                            maxValue: this.getMaxValue(data)
                        }
                    };

                    this.data = data;
                }
            })
        );

        this.subs.push(
            this.googleCombochart.chartImg$.subscribe((chartImgObj: { chartName: string; imgUri: string }) => {
                this.chartToImageSvc.setChartImage(chartImgObj);
            })
        );
    }

    ngOnDestroy(): void {
        this.subs.forEach((x) => x.unsubscribe());
    }
}
